import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../SEO/SEO';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Shield, Home, Lock, FileCheck, CheckCircle2, TriangleAlert, Key, Building2, Landmark, User, FileText, TrendingUp, Download, ArrowRight, Award, Bell, Eye, HandshakeIcon, Info, Gavel } from 'lucide-react';
import './SecuredAssets.css';
import { Handshake } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import axios from 'axios';

const sanitizeAndFormatHtml = (content) => {
  if (!content || typeof content !== 'string') return '';
  let html = content;
  html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
    const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
    if (!attrMatch) return tag;
    const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
    const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
    const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
    return `<a href="${rawUrl}"${targetAttr}>`;
  });
  if (!/<(?:br|p|div)\b/i.test(html)) {
    html = html.replace(/\r?\n/g, '<br />');
  }
  return html;
};

const SecuredAssets = () => {
  const navigate = useNavigate();

  const handleRichTextClick = (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    const linkTarget = target.getAttribute('target');
    if (!href || linkTarget === '_blank') return;

    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('https://ahamhfc.com/') || href.startsWith('http://ahamhfc.com/')) {
      const path = href.replace(/^https?:\/\/ahamhfc\.com/, '');
      if (path.startsWith('/')) {
        e.preventDefault();
        navigate(path);
      }
    }
  };

  const [banner, setBanner] = useState(null);
  const [coreSettings, setCoreSettings] = useState(null);
  const [coreBenefits, setCoreBenefits] = useState([]);
  const [financeSettings, setFinanceSettings] = useState(null);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [assessmentFactors, setAssessmentFactors] = useState([]);
  const [termsSettings, setTermsSettings] = useState(null);
  const [termsItems, setTermsItems] = useState([]);
  const [defaultSettings, setDefaultSettings] = useState(null);
  const [defaultSteps, setDefaultSteps] = useState([]);
  const [releaseSettings, setReleaseSettings] = useState(null);
  const [releaseSteps, setReleaseSteps] = useState([]);
  const [ctaSettings, setCtaSettings] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [bannerRes, settingsRes, benefitsRes, financeRes, typesRes, factorsRes, termsSettingsRes, termsItemsRes, defaultSettingsRes, defaultStepsRes, releaseSettingsRes, releaseStepsRes, ctaRes, tableRes] = await Promise.all([
          axios.get('/api/admin/banners/page/secured_assets'),
          axios.get('/api/admin/secured-assets/core-settings'),
          axios.get('/api/admin/secured-assets/core-benefits'),
          axios.get('/api/admin/secured-assets/finance-settings'),
          axios.get('/api/admin/secured-assets/property-types'),
          axios.get('/api/admin/secured-assets/assessment-factors'),
          axios.get('/api/admin/secured-assets/terms-settings'),
          axios.get('/api/admin/secured-assets/terms-items'),
          axios.get('/api/admin/secured-assets/default-settings'),
          axios.get('/api/admin/secured-assets/default-steps'),
          axios.get('/api/admin/secured-assets/release-settings'),
          axios.get('/api/admin/secured-assets/release-steps'),
          axios.get('/api/admin/cta-banner/settings/secured_assets'),
          axios.get('/api/admin/secured-assets-table/active')
        ]);

        const banners = bannerRes.data.data || [];
        if (banners.length > 0) {
          setBanner(banners[0]);
        }

        if (settingsRes.data.success && settingsRes.data.data) {
          setCoreSettings(settingsRes.data.data);
        }

        if (benefitsRes.data.success) {
          setCoreBenefits((benefitsRes.data.data || []).filter(b => b.is_active));
        }

        if (financeRes.data.success && financeRes.data.data) {
          setFinanceSettings(financeRes.data.data);
        }

        if (typesRes.data.success) {
          setPropertyTypes((typesRes.data.data || []).filter(t => t.is_active));
        }

        if (factorsRes.data.success) {
          setAssessmentFactors((factorsRes.data.data || []).filter(f => f.is_active).map(f => f.factor_text));
        }

        if (termsSettingsRes.data.success && termsSettingsRes.data.data) {
          setTermsSettings(termsSettingsRes.data.data);
        }

        if (termsItemsRes.data.success) {
          setTermsItems((termsItemsRes.data.data || []).filter(i => i.is_active));
        }

        if (defaultSettingsRes.data.success && defaultSettingsRes.data.data) {
          setDefaultSettings(defaultSettingsRes.data.data);
        }

        if (defaultStepsRes.data.success) {
          setDefaultSteps((defaultStepsRes.data.data || []).filter(s => s.is_active));
        }

        if (releaseSettingsRes.data.success && releaseSettingsRes.data.data) {
          setReleaseSettings(releaseSettingsRes.data.data);
        }

        if (releaseStepsRes.data.success) {
          setReleaseSteps((releaseStepsRes.data.data || []).filter(s => s.is_active));
        }

        if (ctaRes.data.success && ctaRes.data.data) {
          setCtaSettings(ctaRes.data.data);
        }

        if (tableRes.data.success) {
          setTableData(tableRes.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching dynamic data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // No longer needed: static data arrays are replaced by database content

  return (
    <div className="secured-assets">

      <SEO
        title="Secured Assets for Sale under SARFAESI | Aham Housing Finance"

        description="View Aham Housing Finance’s secured assets under SARFAESI – updated property auctions, transparent terms and simple steps to participate in the sale."
        keywords="Aham Housing Finance secured assets, Aham secured assets, secured assets possessed, secured asset sale, SARFAESI secured assets, secured creditor property, mortgaged property possession, immovable secured asset, possessed property list, housing finance secured assets, loan recovery property, authorised officer possession notice, secured asset disclosure, NPA secured assets, property under possession, security interest enforcement, Aham asset possession notice, defaulted property list, secured property details, Aham Housing Finance SARFAESI"
        canonical="https://ahamhfc.com/secured-assets-sarfaesi-aham-housing-finance"
        ogTitle="Secured Assets for Sale under SARFAESI | Aham Housing Finance"
        ogDescription="View Aham Housing Finance’s secured assets under SARFAESI – updated property auctions, transparent terms and simple steps to participate in the sale."
      />

      {/* Hero Banner */}
      {/* Hero Banner */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {loading ? (
          <div className="absolute inset-0 bg-primary-900 animate-pulse"></div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('${banner?.image_url || '/img/Banners/Secured-Banner.webp'}')`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-4xl text-left"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white mb-6 border border-white/20">
                  <Shield className="w-5 h-5" />
                  <span className="font-medium">Secured Assets</span>
                </div>
                <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || 'Information on Your Secured Assets'}
                </h1>
                <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-8">
                  {banner?.subtitle || 'Transparency is core to our lending practices. This page provides clear and comprehensive information on how your assets are secured, managed and protected.'}
                </p>

                {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6 md:mb-8"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Secured Asset</span>
                </motion.div>
              </motion.div>
            </div>
          </>
        )}
      </section>

       <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10 text-center px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#2d347c] mb-4">Secured Assets Data</h2>
            {/* <p className="text-slate-600 max-w-3xl mx-auto">
            Comprehensive list of secured assets possessed under SARFAESI Act, transparently displayed with all relevant borrower and property details.
          </p> */}
        </div>
        
        <div className="relative group">
          <div className="overflow-x-auto shadow-2xl rounded-2xl border border-gray-100 bg-white">
            <table className="w-full border-collapse text-left text-sm min-w-[2800px]">
              <thead className="bg-[#2d347c] text-white sticky top-0 z-10">
                <tr>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Sl.</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Branch</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap w-[150px]">State</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Borrower Name</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Guarantor 1</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Guarantor 2</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Guarantor 3</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap w-[350px]">Borrower Address</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap w-[350px]">Guarantor 1 Address</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap w-[350px]">Guarantor 2 Address</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap w-[350px]">Guarantor 3 Address</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Outstanding</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Classification</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Date</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap w-[450px]">Security Details</th>
                  <th className="p-4 border border-blue-800/20 whitespace-nowrap">Title Holder</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="16" className="p-8 text-center text-slate-500 animate-pulse">Loading data...</td></tr>
                ) : tableData.length === 0 ? (
                  <tr><td colSpan="16" className="p-8 text-center text-slate-500">No data available</td></tr>
                ) : (
                  tableData.map((row, index) => (
                    <motion.tr
                      key={row.id || index}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-blue-50/50 transition-colors border-b last:border-0 even:bg-slate-50/30 font-medium"
                    >
                      <td className="p-4 border border-gray-100">{index + 1}</td>
                      <td className="p-4 border border-gray-100 text-slate-700">{row.branch}</td>
                      <td className="p-4 border border-gray-100 text-slate-600 whitespace-nowrap min-w-[150px]">{row.state}</td>
                      <td className="p-4 border border-gray-100 font-bold text-[#2d347c]">{row.borrower_name}</td>
                      <td className="p-4 border border-gray-100 text-slate-700">{row.guarantor_name_1 || '-'}</td>
                      <td className="p-4 border border-gray-100 text-slate-700">{row.guarantor_name_2 || '-'}</td>
                      <td className="p-4 border border-gray-100 text-slate-700">{row.guarantor_name_3 || '-'}</td>
                      <td className="p-4 border border-gray-100 text-xs leading-tight text-slate-600">{row.registered_address_borrower || '-'}</td>
                      <td className="p-4 border border-gray-100 text-xs leading-tight text-slate-600">{row.registered_address_guarantor_1 || '-'}</td>
                      <td className="p-4 border border-gray-100 text-xs leading-tight text-slate-600">{row.registered_address_guarantor_2 || '-'}</td>
                      <td className="p-4 border border-gray-100 text-xs leading-tight text-slate-600">{row.registered_address_guarantor_3 || '-'}</td>
                      <td className="p-4 border border-gray-100 font-bold text-slate-800 whitespace-nowrap">{row.outstanding_amount}</td>
                      <td className="p-4 border border-gray-100 font-bold text-red-600">{row.asset_classification}</td>
                      <td className="p-4 border border-gray-100 text-slate-700 whitespace-nowrap">{row.date_of_asset_classification}</td>
                      <td className="p-4 border border-gray-100 text-xs leading-tight text-slate-600">{row.details_of_security_possessed || '-'}</td>
                      <td className="p-4 border border-gray-100 font-bold italic text-slate-800">{row.name_of_title_holder || '-'}</td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="absolute top-1/2 -right-4 -translate-y-1/2 p-2 bg-white rounded-full shadow-lg border border-gray-100 invisible group-hover:visible lg:flex hidden items-center gap-1 text-[10px] text-slate-400">
            Scroll Right <span className="animate-bounce-x">→</span>
          </div>
        </div>
      </section> 

      {/* Section 1: What is a Secured Asset */}
      

      {/* Section 2: Types of Properties */}
      <section className="properties-section">
        <div className="section-container">
          <div className="section-header-center text-center lg:text-left">
            <span className="section-tag mx-auto lg:mx-0">{financeSettings?.section_tag || 'What We Finance'}</span>
            <h2 className="section-title mx-auto lg:mx-0">{financeSettings?.section_title || 'Types of Secured Properties We Finance'}</h2>
            <p className="section-description mx-auto lg:mx-0">
              {financeSettings?.section_description || 'At Aham HFC, the secured assets we accept cover a wide variety of residential properties, each undergoing rigorous eligibility and verification checks.'}
            </p>
          </div>

          <div className="property-types-grid">
            {(propertyTypes.length > 0 ? propertyTypes : [
              { icon_name: 'Building2', title: 'Residential Flats & Apartments', description: 'Newly built units in approved housing complexes', color: 'blue' },
              { icon_name: 'TrendingUp', title: 'Under-Construction Projects', description: 'Projects with municipal approvals', color: 'gold' },
              { icon_name: 'Home', title: 'Individual Houses', description: 'Independent units in approved layouts', color: 'green' },
              { icon_name: 'Landmark', title: 'Urban Properties', description: 'Located in cities with market viability', color: 'purple' }
            ]).map((property, index) => {
              const IconComponent = {
                Building2, TrendingUp, Home, Landmark, Shield, User, FileCheck, Handshake, CheckCircle2, TriangleAlert, Key, FileText, Download, ArrowRight, Award, Bell, Eye, HandshakeIcon
              }[property.icon_name] || Building2;

              return (
                <Card key={index} className={`property-type-card ${property.color}`}>
                  <div className="property-type-icon"><IconComponent className="w-8 h-8" /></div>
                  <h3 className="property-type-title">{property.title}</h3>
                  <p className="property-type-description">{property.description}</p>
                </Card>
              );
            })}
          </div>

          <Card className="assessment-card">
            <h3 className="assessment-heading">{financeSettings?.assessment_heading || 'Property Assessment Criteria'}</h3>
            <p className="assessment-intro">
              {financeSettings?.assessment_intro || 'Every property is assessed on multiple factors to ensure the security remains robust for both you and us:'}
            </p>
            <div className="assessment-factors">
              {(assessmentFactors.length > 0 ? assessmentFactors : [
                'Clear title verification',
                'Strategic location analysis',
                'Build quality inspection',
                'Future resale value assessment',
                'Local regulation compliance',
                'Market demand evaluation'
              ]).map((factor, index) => (
                <div key={index} className="factor-item">
                  <CheckCircle2 className="factor-icon" />
                  <span>{factor}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      {/* Section 3: Rights & Responsibilities */}
      <section className="rights-responsibilities-section">
        <div className="section-container">
          <div className="section-header-center text-center lg:text-left">
            <span className="section-tag mx-auto lg:mx-0">{termsSettings?.section_tag || 'Know Your Terms'}</span>
            <h2 className="section-title mx-auto lg:mx-0">{termsSettings?.section_title || 'What Borrowers Should Know'}</h2>
          </div>

          <div className="rights-resp-layout">
            <Card className="rights-card">
              <div className="card-header-rr">
                <Key className="header-icon" />
                <h3 className="card-title-rr">{termsSettings?.rights_title || 'Your Rights'}</h3>
              </div>
              <div className="items-list">
                {(termsItems.filter(i => i.category === 'right').length > 0 ? termsItems.filter(i => i.category === 'right') : [
                  { icon_name: 'Home', item_text: 'Full use and occupancy of the property with timely repayments' },
                  { icon_name: 'FileText', item_text: 'Receive all statements, disclosures and updates on secured asset' },
                  { icon_name: 'Key', item_text: 'Right to charge release after full repayment completion' }
                ]).map((right, index) => {
                  const IconComponent = {
                    Home, FileText, Key, CheckCircle2, Shield, Lock, Bell, TriangleAlert, Landmark, Info
                  }[right.icon_name] || Home;

                  return (
                    <div key={index} className="item-row">
                      <div className="item-icon-wrapper green"><IconComponent className="w-5 h-5" /></div>
                      <p className="item-text">{right.item_text}</p>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="responsibilities-card">
              <div className="card-header-rr">
                <Shield className="header-icon" />
                <h3 className="card-title-rr">{termsSettings?.responsibilities_title || 'Your Responsibilities'}</h3>
              </div>
              <div className="items-list">
                {(termsItems.filter(i => i.category === 'responsibility').length > 0 ? termsItems.filter(i => i.category === 'responsibility') : [
                  { icon_name: 'CheckCircle2', item_text: 'Ensure timely payment of instalments and charges' },
                  { icon_name: 'Shield', item_text: 'Maintain property in good condition with necessary repairs' },
                  { icon_name: 'Lock', item_text: 'Do not pledge, sell or alter asset without lender consent' }
                ]).map((resp, index) => {
                  const IconComponent = {
                    Home, FileText, Key, CheckCircle2, Shield, Lock, Bell, TriangleAlert, Landmark, Info
                  }[resp.icon_name] || Shield;

                  return (
                    <div key={index} className="item-row">
                      <div className="item-icon-wrapper gold"><IconComponent className="w-5 h-5" /></div>
                      <p className="item-text">{resp.item_text}</p>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="default-section">
        <div className="section-container">
          <div className="default-header text-center lg:text-left">
            <TriangleAlert className="warning-icon mx-auto lg:mx-0" />
            <div>
              <span className="section-tag mx-auto lg:mx-0">{defaultSettings?.section_tag || 'Transparency & Clarity'}</span>
              <h2 className="section-title-white mx-auto lg:mx-0">{defaultSettings?.section_title || 'What Happens in the Event of Default'}</h2>
              <p className="default-intro mx-auto lg:mx-0">
                {defaultSettings?.section_intro || "While we strive to support all our borrowers and avoid distress situations, it's essential to understand how default scenarios are managed — for transparency and clarity."}
              </p>
            </div>
          </div>

          <div className="default-process-flow">
            {(defaultSteps.length > 0 ? defaultSteps : [
              { step_number: '01', title: 'Notification', description: 'We notify you of payment default or breach', icon_name: 'Bell' },
              { step_number: '02', title: 'Remediation Period', description: 'Reasonable time provided for resolution', icon_name: 'TriangleAlert' },
              { step_number: '03', title: 'Legal Notice', description: 'If unresolved, formal legal process initiated', icon_name: 'FileText' },
              { step_number: '04', title: 'Asset Recovery', description: 'Prescribed auction or recovery measures', icon_name: 'Landmark' },
              { step_number: '05', title: 'Settlement', description: 'Proceeds cover expenses, surplus returned to you', icon_name: 'CheckCircle2' }
            ]).map((item, index) => {
              const IconComponent = {
                Bell, TriangleAlert, FileText, Landmark, CheckCircle2, Info
              }[item.icon_name] || TriangleAlert;

              return (
                <div key={index} className="process-card">
                  <div className="process-step-badge">{item.step_number}</div>
                  <div className="process-icon-wrapper"><IconComponent className="w-5 h-5" /></div>
                  <h3 className="process-title">{item.title}</h3>
                     <p className="process-description text-white/85 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>

          {(defaultSettings?.auction_notice_text || defaultSettings?.auction_notice_text === undefined) && (
            <Card className="commitment-note auction-notice-card" onClick={handleRichTextClick}>
              <Gavel className="commitment-icon auction-icon" />
              <div className="commitment-text">
                <p
                  dangerouslySetInnerHTML={{
                    __html: sanitizeAndFormatHtml(
                      defaultSettings?.auction_notice_text ||
                      'Interested buyers can review the <a href="https://ahamhfc.com/aham-housing-finance-e-auction-sale-notice">current e-auction sale notices</a> for eligible secured properties offered by Aham Housing Finance.'
                    )
                  }}
                />
              </div>
            </Card>
          )}

          <Card className="commitment-note">
            <Eye className="commitment-icon" />
            <div className="commitment-text">
              <p>
                {defaultSettings?.commitment_text_1 || 'The proceeds from sale of the secured property will first cover charged expenses, loan outstanding, interest, and then any surplus will be returned to you.'}
              </p>
              <p><strong>{defaultSettings?.commitment_text_2 || 'We commit to keeping you informed at every step of this process.'}</strong></p>
            </div>
          </Card>
        </div>
      </section>

      {/* Section 5: Release of Security */}
      <section className="release-section">
        <div className="section-container">
          <div className="release-header text-center lg:text-left">
            <Award className="award-icon mx-auto lg:mx-0" />
            <span className="section-tag mx-auto lg:mx-0">{releaseSettings?.section_tag || 'Successful Completion'}</span>
            <h2 className="section-title mx-auto lg:mx-0">{releaseSettings?.section_title || 'When Your Loan Is Fully Repaid'}</h2>
            <p className="release-intro mx-auto lg:mx-0">
              {releaseSettings?.section_intro || "Congratulations — once you've successfully completed repayment, here's what happens:"}
            </p>
          </div>

          <div className="release-process">
            {(releaseSteps.length > 0 ? releaseSteps : [
              { step_number: '1', title: 'Charge Release Process', description: 'Formal release of lender charge initiated' },
              { step_number: '2', title: 'NOC Issuance', description: 'No-objection certificate provided promptly' },
              { step_number: '3', title: 'Full Control', description: 'Property free from any encumbrance' },
              { step_number: '4', title: 'Final Documentation', description: 'Complete statements and confirmation' }
            ]).map((step, index) => (
              <div key={index} className="release-step">
                <div className="release-number">{step.step_number}</div>
                <div className="release-content">
                  <h3 className="release-title">{step.title}</h3>
                  <p className="release-description">{step.description}</p>
                </div>
                {index < (releaseSteps.length > 0 ? releaseSteps.length : 4) - 1 && (
                  <ArrowRight className="release-arrow" />
                )}
              </div>
            ))}
          </div>

          <Card className="success-card">
            <div className="success-icon-wrapper">
              <Award className="success-icon" />
            </div>
            <h3 className="success-heading">{releaseSettings?.footer_heading || 'Your Achievement, Our Shared Success'}</h3>
            <p className="success-text">
              {releaseSettings?.footer_text || 'Successfully closing a housing loan is a momentous milestone. At Aham Housing Finance, we are honored to have been part of your home-owning journey and are committed to supporting your future financial goals.'}
            </p>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="assets-cta">
        <div className="assets-cta-container">
          <Shield className="cta-shield-icon" />
          <h2 className="cta-assets-title">{ctaSettings?.title || 'Need More Information?'}</h2>
          <p className="cta-assets-text">
            {ctaSettings?.description || 'Download our complete guide on secured assets or contact our customer support team for personalized assistance.'}
          </p>
          <div className="cta-assets-actions">
            <Button
              variant="outline"
              className="cta-assets-secondary"
              onClick={() => navigate(ctaSettings?.button_link || "/contact")}
            >
              {ctaSettings?.button_text || 'Contact Support Team'}
            </Button>
          </div>
          {ctaSettings?.bottom_text && (
            <div className="cta-assets-bottom-text mt-8 text-white/70 text-sm italic">
              {ctaSettings.bottom_text}
            </div>
          )}
        </div>
      </section> */}
    </div>
  );
};

export default SecuredAssets;